import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { IBAN_COMPONENT_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds.ts';
import { Flex, Paper, TextInput } from '@mantine/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { useEffect, useRef, useState } from 'react';
import type { BankAccountCountryOption } from '../types.ts';
import { Icon } from '../../fundament/Icon/Icon.tsx';
import { BANK_COUNTRY_OPTION_HEIGHT, BankCountryOption } from './BankCountryOption.tsx';

interface Props {
	options: BankAccountCountryOption[];
	value: string | null;
	onHighlight: (countryCode: string) => void;
}

export const BankCountryPicker = ({ options, value, onHighlight }: Props) => {
	const tCapManage = useTranslationCapManage();
	const [search, setSearch] = useState('');
	const listRef = useRef<HTMLDivElement>(null);

	const query = search.trim().toLowerCase();
	const filteredOptions = query
		? options.filter((option) => option.label.toLowerCase().includes(query))
		: options;

	const virtualizer = useVirtualizer({
		count: filteredOptions.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => BANK_COUNTRY_OPTION_HEIGHT,
		overscan: 10,
	});

	useEffect(() => {
		const selectedIndex = options.findIndex((option) => option.value === value);

		if (selectedIndex >= 0) {
			virtualizer.scrollToIndex(selectedIndex, { align: 'center' });
		}
	}, []);

	return (
		<Paper withBorder p="sm">
			<Flex direction="column" gap="0.5rem">
				<TextInput
					size="md"
					value={search}
					onChange={(event) => setSearch(event.currentTarget.value)}
					placeholder={tCapManage('bank_account.drawer.country_search_placeholder')}
					leftSection={<Icon variant="search-v3" size="lg" color="black" />}
					data-testid={IBAN_COMPONENT_ELEMENT_ID.BANK_COUNTRY_SEARCH_INPUT}
				/>
				<div ref={listRef} style={{ height: '16rem', overflowY: 'auto' }}>
					<div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
						{virtualizer.getVirtualItems().map((virtualRow) => {
							const option = filteredOptions[virtualRow.index];
							return (
								<div
									key={option.value}
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: '100%',
										transform: `translateY(${virtualRow.start}px)`,
									}}
								>
									<BankCountryOption
										option={option}
										isSelected={option.value === value}
										onHighlight={onHighlight}
									/>
								</div>
							);
						})}
					</div>
				</div>
			</Flex>
		</Paper>
	);
};
